import { Save } from '@mui/icons-material';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { yellow } from '@mui/material/colors';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import messages from '../../intl/messages';
import { fetchClusterSchedule, saveClusterSchedule } from '../../store/model/actions';
import { getClusterScheduleConfig } from '../../store/model/selectors';
import Switch from '../Switch';
import UniButtton from '../UniButtton';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (h) => `${h}:00`;

const useStyles = makeStyles()((theme) => ({
	bounding: {
		padding: '0.7rem 2rem 2rem 2rem',
		fontSize: '0.9rem',
		'& .headingContainer': {
			display: 'flex',
			alignItems: 'flex-start',
		},
		'& h2': {
			display: 'inline-block',
			fontSize: '1.2rem',
		},
		'& .saveIcon': { float: 'right', marginTop: '7px' },
		'&.draft': {
			background: yellow['100'],
		},
		'& .scheduleRow': {
			display: 'flex',
			alignItems: 'center',
			gap: '16px',
			marginTop: '8px',
		},
		'& .scheduleLabel': {
			minWidth: '220px',
			display: 'flex',
			alignItems: 'center',
			gap: '4px',
		},
	},
}));

const defaultConfig = {
	shutdownEnabled: false,
	shutdownHour: 22,
	startupEnabled: false,
	startupHour: 7,
};

const ScalingClusterSchedule = () => {
	const dispatch = useDispatch();
	const reduxConfig = useSelector(getClusterScheduleConfig);
	const [localConfig, setLocalConfig] = useState(defaultConfig);
	const [isDraft, setIsDraft] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const { classes } = useStyles();

	useEffect(() => {
		dispatch(fetchClusterSchedule());
	}, [dispatch]);

	useEffect(() => {
		if (reduxConfig) {
			setLocalConfig(reduxConfig);
		}
	}, [reduxConfig]);

	useEffect(() => {
		if (!reduxConfig) return;
		const changed =
			localConfig.shutdownEnabled !== reduxConfig.shutdownEnabled ||
			localConfig.shutdownHour !== reduxConfig.shutdownHour ||
			localConfig.startupEnabled !== reduxConfig.startupEnabled ||
			localConfig.startupHour !== reduxConfig.startupHour;

		if (changed) {
			window.onbeforeunload = () => false;
			setIsDraft(true);
		} else {
			window.onbeforeunload = () => {};
			setIsDraft(false);
		}
	}, [localConfig, reduxConfig]);

	const handleSave = async () => {
		setIsSaving(true);
		await dispatch(saveClusterSchedule(localConfig));
		setIsSaving(false);
		setIsDraft(false);
		window.onbeforeunload = () => {};
	};

	return (
		<Paper elevation={3} className={`${classes.bounding} ${isDraft && 'draft'}`}>
			{/* Header */}
			<Grid container spacing={0} direction="row" justifyContent="space-evenly" alignItems="flex-start">
				<Grid item xs={3} className="headingContainer">
					<h2>
						<FormattedMessage {...messages.clusterScheduler} />
					</h2>
				</Grid>
				<Grid item xs={8} />
				<Grid item xs={1}>
					{isSaving ? (
						<span className="saveIcon">
							<FormattedMessage {...messages.uploading} /> ...
						</span>
					) : (
						<UniButtton
							tooltip={<FormattedMessage {...messages[isDraft ? 'saveInstructions' : 'noChanges']} />}
							color="primary"
							onClick={handleSave}
							icon={<Save fontSize="large" />}
							className="saveIcon"
							disabled={!isDraft}
						/>
					)}
				</Grid>
			</Grid>

			{/* Row 1: Auto shutdown */}
			<div className="scheduleRow">
				<div className="scheduleLabel">
					<FormattedMessage {...messages.autoShutdown} />
					<Switch
						checked={localConfig.shutdownEnabled}
						handleChange={() =>
							setLocalConfig((prev) => ({ ...prev, shutdownEnabled: !prev.shutdownEnabled }))
						}
					/>
				</div>
				<FormControl variant="outlined" size="small" style={{ minWidth: 140 }} disabled={!localConfig.shutdownEnabled}>
					<InputLabel>
						<FormattedMessage {...messages.shutdownHourLabel} />
					</InputLabel>
					<Select
						value={localConfig.shutdownHour}
						label={<FormattedMessage {...messages.shutdownHourLabel} />}
						onChange={(e) =>
							setLocalConfig((prev) => ({ ...prev, shutdownHour: e.target.value }))
						}
					>
						{HOURS.map((h) => (
							<MenuItem key={h} value={h}>
								{formatHour(h)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>

			{/* Row 2: Auto startup */}
			<div className="scheduleRow">
				<div className="scheduleLabel">
					<FormattedMessage {...messages.autoStartup} />
					<Switch
						checked={localConfig.startupEnabled}
						handleChange={() =>
							setLocalConfig((prev) => ({ ...prev, startupEnabled: !prev.startupEnabled }))
						}
					/>
				</div>
				<FormControl variant="outlined" size="small" style={{ minWidth: 140 }} disabled={!localConfig.startupEnabled}>
					<InputLabel>
						<FormattedMessage {...messages.startupHourLabel} />
					</InputLabel>
					<Select
						value={localConfig.startupHour}
						label={<FormattedMessage {...messages.startupHourLabel} />}
						onChange={(e) =>
							setLocalConfig((prev) => ({ ...prev, startupHour: e.target.value }))
						}
					>
						{HOURS.map((h) => (
							<MenuItem key={h} value={h}>
								{formatHour(h)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
		</Paper>
	);
};

export default ScalingClusterSchedule;
